import {
  Avatar,
  styled,
  Typography,
  Badge,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import { get, isUndefined } from 'lodash-es';
import React from 'react';
import { ProfileTypes } from '../../constants/profileType.constant';
import useStore from '../../stores/store';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import { getAvatarText } from '../../utils/stringHelpers';
import StyledBoldTypography from '../Styled/StyledBoldTypography';
import TimeSincePost from './timeSincePost';
import PostType, { CampaignType } from '@/constants/postType.constants';
import { Disclosure, Transition } from '@headlessui/react';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { getJobTitle } from '@/utils/user.helper';

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 22,
  height: 22,
  border: `2px solid ${theme.palette.background.innerGray}`,
  backgroundColor: theme.palette.primary.lightbg,
  // padding: 2,
}));


function PostContributorCard(props) {
  const { contributor, type } = props;

  const currencySymbol = getSymbolFromCurrency(contributor.currency);
  const isNPAdmin = useStore((state) => state.user.isNonProfitAdmin);
  // const jobTitle =
  //   isNPAdmin && contributor.orgDetails
  //     ? `${contributor.designation} - ${contributor.orgDetails.name}`
  //     : contributor.designation;

  const avatar =
    contributor.as === ProfileTypes.Organization
      ? contributor.orgDetails.logoUrl
      : contributor.avatar;
  const name =
    contributor.as === ProfileTypes.Organization
      ? contributor.orgDetails.name
      : `${contributor.first_name} ${contributor.last_name}`;

  const matchedAmount = get(contributor, 'matchedAmount', 0);
  const totalDonationAmount =
    get(contributor, 'donationAmount', 0) +
    get(contributor, 'matchedAmount', 0);
  // const isGiftOfGivingDonation =
  //   get(contributor, 'paymentProvider') === 'gift of giving';

  const isGiftOfGivingDonation =
    get(contributor, 'donationType') === 'GIFT_OF_GIVING_COMPANY_DONATION';

  const isStatusPledged = get(contributor, 'status') === 'pledged';

  const totalDonatedItems = contributor?.items?.reduce(
    (total, item) => total + item.quantity,
    0,
  );

  const getIndicatorIcon = () => {
    switch (type) {
      case CampaignType.CollectGoods:
        return '/assets/icons/inkind-campaign.svg';
      case PostType.Volunteering:
        return '/assets/icons/volunteer.svg';
      case CampaignType.FundRaise:
        return '/assets/icons/fundraise-campaign.svg';
      case PostType.CrowdFunding:
        return '/assets/icons/support.svg';
      default:
        return '';
    }
  };

  const renderContributions = () => {
    switch (type) {
      case CampaignType.CollectGoods:
        return renderInKindDonations();
      case PostType.Volunteering:
        return renderVolunteerings();
      case CampaignType.FundRaise:
        return renderDonations();
      case PostType.CrowdFunding:
        return renderCrowdFundingContributions();
      default:
        return <></>;
    }
  };

  const renderCrowdFundingContributions = () => {
    return (
      <>
        {totalDonationAmount > 0 && (
          <div className="flex gap-2">
            <Typography variant="body2" color="textSecondary">
              {currencySymbol}
              {totalDonationAmount}
            </Typography>
            {isStatusPledged && (
              <Typography variant="body2" color="textSecondary">
                pledged
              </Typography>
            )}
          </div>
        )}
      </>
    );
  };

  const renderDonations = () => {
    return (
      <>
        {totalDonationAmount > 0 && (
          <div className="flex gap-2">
            <Typography variant="body2" color="textSecondary">
              {currencySymbol}
              {totalDonationAmount}
            </Typography>
            {isGiftOfGivingDonation && (
              <Typography variant="body2" color="textSecondary">
                (Gift Of Giving)
              </Typography>
            )}
            {matchedAmount > 0 && (
              <Typography variant="body2" color="textSecondary">
                ({currencySymbol}
                {matchedAmount} Org Match)
              </Typography>
            )}
            {isStatusPledged && (
              <Typography variant="body2" color="textSecondary">
                pledged
              </Typography>
            )}
          </div>
        )}
      </>
    );
  };

  const renderInKindDonations = () => {
    return (
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex">
              <div className="flex flex-col">
                <Typography variant="body2" className="">
                  {totalDonatedItems}{' '}
                  {`item${totalDonatedItems > 1 ? 's' : ''} ${
                    contributor.status === 'pledged' ? 'pledged' : 'donated'
                  }`}
                </Typography>
              </div>
              <MdOutlineKeyboardArrowDown
                className={`flex items-center justify-center ${
                  open ? 'rotate-180 transform' : ''
                } h-6 w-6 text-text-secondary`}
              />
            </Disclosure.Button>
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0">
              <Disclosure.Panel className="flex">
                <div className="ml-4">
                  {contributor?.items?.map((item) => (
                    <div
                      className="my-1 flex items-center gap-1"
                      key={item.title}>
                      <Typography
                        key={item.title}
                        variant="body2"
                        color="textSecondary"
                        className="w-full break-all">
                        {item.quantity} {item.title}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    );
  };

  const renderVolunteerings = () => {
    return (
      <>
        {!isUndefined(contributor.effort) && (
          <Typography variant="body2" color="textSecondary">
            {contributor.effort > 0
              ? `${contributor.effort} hour${
                  contributor.effort > 1 ? 's' : ''
                } logged`
              : 'No effort logged'}
          </Typography>
        )}
      </>
    );
  };

  return (
    <div className="flex w-full flex-col py-1">
      <ListItem alignItems="flex-start" className="p-0">
        <ListItemAvatar>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <SmallAvatar alt="smallicon" src={getIndicatorIcon()} />
            }>
            <Avatar alt="user avatar" src={getQualifiedImageUrl(avatar)}>
              {getAvatarText(name)}
            </Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <StyledBoldTypography variant="subtitle1">
              {name}
            </StyledBoldTypography>
          }
          secondary={
            <>
              <Typography variant="body2" color="textSecondary">
                {getJobTitle(contributor)}
              </Typography>
              <TimeSincePost
                createdOn={contributor.createdAt}
                variant="body2"
                color="textSecondary"
              />
            </>
          }
        />
      </ListItem>
      <div className="ml-14 mt--2">
        {renderContributions()}
        <Divider className="py-1.5" />
      </div>
      {/* <div className="ml-12 flex gap-2">
        {!isUndefined(contributor.effort) && (
          <StyledBoldTypography variant="body1" color="primary">
            {contributor.effort > 0 ? `${contributor.effort} hours` : '-'}
          </StyledBoldTypography>
        )}
        {totalDonationAmount > 0 && (
          <div className="flex gap-2">
            <StyledBoldTypography variant="body1" color="primary">
              {currencySymbol}
              {totalDonationAmount}
            </StyledBoldTypography>
            {isGiftOfGivingDonation && (
              <Typography variant="body2" color="textSecondary">
                (Gift Of Giving)
              </Typography>
            )}
            {matchedAmount > 0 && (
              <Typography variant="body2" color="textSecondary">
                ({currencySymbol}
                {matchedAmount} org match)
              </Typography>
            )}
          </div>
        )}

        {contributor.items && (
          // <div className="flex gap-2">
          <div
            className="flex gap-2"
            // color="primary"
            // variant="body1"
            // // lineClamp={3}
            // fontWeight={700}
          >
            {contributor.items
              .map((item) => `${item.quantity} ${item.title}`)
              .join(',')}
          </div>
          // </div>
        )}
      </div> */}
    </div>
  );
}

export default PostContributorCard;
